import React from 'react'
import { Prompt } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Radio from '@mui/material/Radio'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Add from '@mui/icons-material/Add'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'

import Title from '../../component/Title'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, FORM_SUB_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { APP_NOTICE_IMAGE_LEAVE_MESSAGE } from './appNoticeLeaveGuard'
import { APP_NOTICE_IMAGE_FILE_ACCEPT } from './appNoticeFormUtils'

const getDatePickerValue = (value) => {
    if (!value) {
        return new Date()
    }

    const nextDate = value instanceof Date ? value : new Date(value)

    return Number.isNaN(nextDate.getTime()) ? new Date() : nextDate
}

const sectionSx = {
    ...FORM_PANEL_SX,
    boxShadow: 'none',
}

const getChoiceCardSx = (selected, disabled = false) => ({
    ...getFormChoiceCardSx(selected, disabled),
    px: 1.25,
    py: 1.25,
    width: '100%',
    '& .MuiRadio-root': {
        mt: -0.1,
        mr: 0.75,
        p: 0.25,
    },
    '& .MuiFormControlLabel-label': {
        width: '100%',
    },
})

const settingGroupPanelSx = {
    ...FORM_SUB_PANEL_SX,
}

const POPUP_STATUS_OPTIONS = [
    {
        value: 0,
        title: '미팝업',
        description: '목록과 상세 공지만 노출합니다. 팝업 이미지와 기간 입력은 생략됩니다.',
        chipLabel: '기본',
        chipColor: 'default',
    },
    {
        value: 1,
        title: '팝업',
        description: '앱 홈 팝업으로 노출합니다. 기간과 팝업 이미지가 필요합니다.',
        chipLabel: '필수 입력',
        chipColor: 'warning',
    },
]

const DEVICE_TYPE_OPTIONS = [
    { value: 'all', title: '전체', description: 'Android와 iOS 사용자 모두에게 노출합니다.' },
    { value: 'and', title: 'Android', description: 'Android 사용자에게만 노출합니다.' },
    { value: 'ios', title: 'iOS', description: 'iOS 사용자에게만 노출합니다.' },
]

const NOTICE_STATUS_OPTIONS = [
    { value: 0, title: '미노출', description: '관리자에서만 보관하고 사용자 화면에는 표시하지 않습니다.' },
    { value: 1, title: '노출', description: '공지 목록과 상세 화면에 표시합니다.' },
]

// 값의 의미가 셋으로 갈린다.
//   kr, th …   해당 국가 사용자에게만
//   99         기타 해외 — 전용 선택지가 없는 나라들 (국내·태국은 제외된다)
//   00         전 세계 (과거 데이터에만 있고 선택지로는 주지 않는다)
//
// ★ 국가를 추가할 때는 서버의 DEDICATED_NOTICE_CCODES(homeControllers getPopupNotice)도
//   함께 고쳐야 한다. 여기에만 넣으면 그 나라 사용자가 자기 공지와 기타 해외 공지를
//   번갈아 보게 된다.
const COUNTRY_OPTIONS = [
    { value: 'kr', title: '국내', description: '국내 사용자 화면에 노출합니다.' },
    { value: '99', title: '기타 해외', description: '국내와 태국을 제외한 나라 사용자 화면에 노출합니다.' },
    { value: 'th', title: '태국', description: '태국 사용자 화면에만 노출합니다. 푸시는 국가를 가리지 않으므로 미발송으로 두세요.' },
]

const PUSH_OPTIONS = [
    { value: 0, title: '미발송', description: '공지 저장만 진행하고 푸시는 보내지 않습니다.' },
    { value: 1, title: '발송', description: '저장 시 사용자에게 푸시를 발송합니다.' },
]

// 긴 폼을 운영 목적별 섹션으로 나눠 입력 흐름을 빠르게 파악하게 한다.
function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={sectionSx}>
            <Stack spacing={1.5}>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {title}
                    </Typography>
                    {description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {description}
                        </Typography>
                    )}
                </Box>
                {children}
            </Stack>
        </Paper>
    )
}

// 화면 동작이 달라지는 주요 선택지는 카드 안에 설명을 함께 표시한다.
function ChoiceCardLabel({
    title,
    description,
    chipLabel,
    chipColor,
}) {
    return (
        <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                {chipLabel && <Chip label={chipLabel} color={chipColor} size="small" variant="outlined" />}
            </Box>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Stack>
    )
}

// 카드형 선택 UI에서도 기존 폼 변경 핸들러가 받던 name/value 이벤트 계약을 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function SettingChoiceGroup({
    title,
    description,
    name,
    value,
    options,
    disabled,
    onChange,
    columns = { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
}) {
    const content = (
        <>
            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="caption" color="text.secondary">
                        {description}
                    </Typography>
                )}
            </Box>
            <Box
                role="radiogroup"
                aria-label={title}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: columns,
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const checked = String(value) === String(option.value)

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name={name}
                                    checked={checked}
                                    onChange={() => onChange(createFieldChangeEvent(name, option.value))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(checked, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                    chipLabel={option.chipLabel}
                                    chipColor={option.chipColor}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </>
    )

    return (
        <FormControl fullWidth>
            {description ? (
                <Box sx={settingGroupPanelSx}>
                    {content}
                </Box>
            ) : content}
        </FormControl>
    )
}

// 이미지 미리보기와 파일 선택 버튼을 같은 카드 안에 묶어 현재 상태를 분명히 보여준다.
function ImageUploadPanel({
    title,
    description,
    imageSrc,
    inputId,
    inputName,
    buttonLabel,
    alt,
    disabled,
    uploadType,
    required,
    onUploadClick,
}) {
    return (
        <Stack spacing={1}>
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {title}
                    </Typography>
                    {required && <Chip label="필수" color="warning" size="small" variant="outlined" />}
                </Box>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box
                sx={{
                    ...FORM_IMAGE_PREVIEW_FRAME_SX,
                    minHeight: 180,
                    height: 'auto',
                }}
            >
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt={alt}
                        sx={{
                            width: '100%',
                            maxHeight: 260,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        선택된 이미지가 없습니다.
                    </Typography>
                )}
            </Box>
            <FormControl fullWidth>
                <TextField
                    style={{ display: "none" }}
                    name={inputName}
                    id={inputId}
                    type="file"
                    disabled={disabled}
                    inputProps={{ accept: APP_NOTICE_IMAGE_FILE_ACCEPT }}
                    onChange={(event) => onUploadClick(event, uploadType)}
                />
                <label htmlFor={inputId}>
                    <Button
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        {buttonLabel}
                    </Button>
                </label>
            </FormControl>
        </Stack>
    )
}

// App 공지사항 등록/수정 화면의 실제 입력 UI를 공유한다.
export default function AppNoticeFormPage({
    title,
    formState,
    listState,
    snackbar,
    handlers,
}) {
    const {
        params,
        push,
        image,
        popupImage,
        saving,
        leaveGuardEnabled,
        detailImageButtonLabel,
    } = formState
    const {
        search: listSearch,
        curPage: listCurPage,
        searchText: listSearchText,
        searchSelect: listSearchSelect,
    } = listState
    const {
        open: openSnackbar,
        severity: severitySnackbar,
        message: messageSnackbar,
        onClose: onSnackbarClose,
    } = snackbar
    const {
        onSubmit,
        onBackClick,
        onFieldChange,
        onDateChange,
        onPushChange,
        onUploadClick,
    } = handlers
    const popupStartTime = getDatePickerValue(params.startTime)
    const popupEndTime = getDatePickerValue(params.endTime)
    const popupEnabled = Number(params.popupStatus) === 1

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>{title}</Title>
                <Prompt when={leaveGuardEnabled} message={APP_NOTICE_IMAGE_LEAVE_MESSAGE} />
                <Box component="form" encType="multipart/form-data" onSubmit={onSubmit}>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={7}>
                            <Stack spacing={2}>
                                <FormSection
                                    title="공지 내용"
                                    description="앱 공지사항 목록과 상세 화면에 표시될 기본 문구입니다."
                                >
                                    <TextField
                                        label="제목"
                                        required
                                        fullWidth
                                        id="title"
                                        name="title"
                                        value={params.title || ''}
                                        disabled={saving}
                                        onChange={onFieldChange}
                                        size="small"
                                    />
                                    <TextField
                                        label="내용"
                                        fullWidth
                                        id="content"
                                        name="content"
                                        multiline
                                        rows={13}
                                        value={params.content || ''}
                                        disabled={saving}
                                        onChange={onFieldChange}
                                        size="small"
                                    />
                                </FormSection>

                                <FormSection
                                    title="상세 이미지"
                                    description="공지 상세 화면에 함께 표시되는 이미지입니다."
                                >
                                    <ImageUploadPanel
                                        title="상세 이미지"
                                        description="앱 공지 상세에서 본문과 함께 노출됩니다."
                                        imageSrc={image}
                                        inputId="image"
                                        inputName="image"
                                        buttonLabel={detailImageButtonLabel}
                                        alt="상세 이미지"
                                        disabled={saving}
                                        uploadType={2}
                                        onUploadClick={onUploadClick}
                                    />
                                </FormSection>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Stack spacing={2}>
                                <FormSection
                                    title="팝업 설정"
                                    description="앱 홈 화면 팝업 노출 여부와 노출 기간을 설정합니다."
                                >
                                    <SettingChoiceGroup
                                        title="앱 홈 팝업"
                                        name="popupStatus"
                                        value={params.popupStatus ?? 0}
                                        options={POPUP_STATUS_OPTIONS}
                                        disabled={saving}
                                        onChange={onFieldChange}
                                    />
                                    {popupEnabled && (
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1,
                                                borderColor: 'warning.light',
                                                bgcolor: 'rgba(255, 243, 224, 0.42)',
                                            }}
                                        >
                                            <Stack spacing={1.5}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                        팝업 노출 기간
                                                    </Typography>
                                                    <Chip label="필수" color="warning" size="small" variant="outlined" />
                                                </Box>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                                            <DesktopDatePicker
                                                                required
                                                                inputFormat="yyyy-MM-dd"
                                                                mask={"____-__-__"}
                                                                label="팝업 시작일"
                                                                disabled={saving}
                                                                value={popupStartTime}
                                                                onChange={(date) => onDateChange('startTime', date)}
                                                                renderInput={(datePickerParams) => <TextField required fullWidth {...datePickerParams} size="small" disabled={saving}/>}
                                                            />
                                                        </LocalizationProvider>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                                            <DesktopDatePicker
                                                                inputFormat="yyyy-MM-dd"
                                                                mask={"____-__-__"}
                                                                label="팝업 종료일"
                                                                disabled={saving}
                                                                value={popupEndTime}
                                                                minDate={popupStartTime}
                                                                onChange={(date) => onDateChange('endTime', date)}
                                                                renderInput={(datePickerParams) => <TextField required fullWidth {...datePickerParams} size="small" disabled={saving} />}
                                                            />
                                                        </LocalizationProvider>
                                                    </Grid>
                                                </Grid>
                                            </Stack>
                                        </Paper>
                                    )}
                                    {!popupEnabled && (
                                        <Alert severity="info" variant="outlined" sx={{ borderRadius: 1 }}>
                                            미팝업 상태에서는 팝업 노출 기간과 팝업 이미지를 저장 전 검증에서 제외합니다.
                                        </Alert>
                                    )}
                                </FormSection>

                                <FormSection
                                    title="팝업 이미지"
                                    description={popupEnabled
                                        ? '앱 홈 팝업에 노출되는 이미지입니다.'
                                        : '미팝업 상태에서는 팝업 이미지가 필요하지 않습니다.'}
                                >
                                    {popupEnabled ? (
                                        <ImageUploadPanel
                                            title="팝업 이미지"
                                            description="팝업 선택 시 필수입니다."
                                            imageSrc={popupImage}
                                            inputId="popupImage"
                                            inputName="popupImage"
                                            buttonLabel="팝업 이미지 업로드"
                                            alt="팝업 이미지"
                                            disabled={saving}
                                            uploadType={1}
                                            required
                                            onUploadClick={onUploadClick}
                                        />
                                    ) : (
                                        <Alert severity="info" variant="outlined" sx={{ borderRadius: 1 }}>
                                            현재 미팝업 상태라 팝업 이미지를 등록하지 않아도 됩니다.
                                        </Alert>
                                    )}
                                </FormSection>
                            </Stack>
                        </Grid>

                        <Grid item xs={12}>
                            <FormSection
                                title="노출/발송 설정"
                                description="대상 기기, 국가, 노출 상태와 푸시 발송 여부를 마지막으로 확인합니다."
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                        gap: 1.5,
                                    }}
                                >
                                    <Box>
                                        <SettingChoiceGroup
                                            title="노출기종"
                                            description="공지 대상 앱 기기를 선택합니다."
                                            name="deviceType"
                                            value={params.deviceType || 'all'}
                                            options={DEVICE_TYPE_OPTIONS}
                                            disabled={saving}
                                            onChange={onFieldChange}
                                            columns={{ xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }}
                                        />
                                    </Box>
                                    <Box>
                                        <SettingChoiceGroup
                                            title="노출여부"
                                            description="사용자 화면 표시 여부를 선택합니다."
                                            name="status"
                                            value={params.status ?? 0}
                                            options={NOTICE_STATUS_OPTIONS}
                                            disabled={saving}
                                            onChange={onFieldChange}
                                        />
                                    </Box>
                                    <Box>
                                        <SettingChoiceGroup
                                            title="노출나라"
                                            description="공지 노출 국가 범위를 선택합니다."
                                            name="cCode"
                                            value={params.cCode || 'kr'}
                                            options={COUNTRY_OPTIONS}
                                            disabled={saving}
                                            onChange={onFieldChange}
                                        />
                                    </Box>
                                    <Box>
                                        <SettingChoiceGroup
                                            title="푸시발송"
                                            description="저장 시 푸시 발송 여부를 선택합니다."
                                            name="push"
                                            value={push}
                                            options={PUSH_OPTIONS}
                                            disabled={saving}
                                            onChange={onPushChange}
                                        />
                                    </Box>
                                </Box>
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        variant="formAction"
                        path="/appNotices"
                        search={listSearch}
                        visible={true}
                        curPage={listCurPage}
                        searchText={listSearchText}
                        searchSelect={listSearchSelect}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        onBackClick={onBackClick}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={onSnackbarClose}/>}
            </Paper>
        </Grid>
    )
}
